/**
 * Навигация ленты: сферы, страны, механики, сортировка и поиск.
 *
 * Разбивка идёт сверху вниз по тому, как человек сужает выбор: сначала «где я
 * это покупаю» (сфера), потом «где я нахожусь» (страна), потом «во что это
 * играется» (механика). Поиск стоит рядом со сферами, а не вместо них: искать
 * по названию можно, только если уже знаешь, что ищешь.
 *
 * Состояние живёт в адресе страницы, а не в компоненте: ссылку на «Напитки в
 * Узбекистане» можно отправить другу, и кнопка «назад» работает как ожидается.
 */
import type { Facet, FeedSort } from '@allswipes/client';
import { Flag, Icon, num, type IconName } from '@allswipes/ui';
import { useT } from '@allswipes/i18n';
import { useEffect, useRef, useState } from 'react';

export interface FeedQuery {
  sphere: string;
  region: string;
  recipe: string;
  sort: FeedSort;
  q: string;
}

export const EMPTY_QUERY: FeedQuery = { sphere: '', region: '', recipe: '', sort: '', q: '' };

const SORTS: Array<{ value: FeedSort; label: string }> = [
  { value: '', label: 'По умолчанию' },
  { value: 'ending', label: 'Скоро закончатся' },
  { value: 'new', label: 'Новые' },
  { value: 'popular', label: 'Больше участников' },
];

export function FeedFilters({
  query,
  spheres,
  regions,
  recipes,
  found,
  onChange,
}: {
  query: FeedQuery;
  spheres: Facet[];
  regions: Facet[];
  recipes: Facet[];
  found: number;
  onChange: (next: Partial<FeedQuery>) => void;
}) {
  const t = useT();
  const [more, setMore] = useState(Boolean(query.recipe || query.sort));
  const narrowed = Boolean(query.sphere || query.region || query.recipe || query.q);

  return (
    <div className="filters">
      <div className="topics" role="tablist" aria-label={t('Сферы')}>
        <TopicTab
          active={query.sphere === ''}
          icon="flame"
          label={t('Все')}
          onClick={() => onChange({ sphere: '' })}
        />
        {spheres.map((s) => (
          <TopicTab
            key={s.key}
            active={query.sphere === s.key}
            icon={(s.icon ?? 'building') as IconName}
            label={t(s.label)}
            count={s.count}
            onClick={() => onChange({ sphere: query.sphere === s.key ? '' : s.key })}
          />
        ))}
      </div>

      <div className="frow">
        <Search value={query.q} onChange={(q) => onChange({ q })} />

        <div className="chips" aria-label={t('Страны')}>
          {/* «Все» стоит первым и выбран по умолчанию: лента начинается со
              всей платформы, а страна — это сужение, а не стартовое условие. */}
          <Chip
            active={query.region === ''}
            label={t('Все страны')}
            count={regions.reduce((sum, r) => sum + r.count, 0)}
            onClick={() => onChange({ region: '' })}
          />
          {regions.map((r) => (
            <Chip
              key={r.key}
              active={query.region === r.key}
              label={t(r.label)}
              count={r.count}
              flag={r.key}
              onClick={() => onChange({ region: query.region === r.key ? '' : r.key })}
            />
          ))}
        </div>

        <span className="spacer" />

        <button
          type="button"
          className={`chip ${more ? 'on' : ''}`}
          aria-expanded={more}
          onClick={() => setMore(!more)}
        >
          <Icon name="settings" size={13} />
          {t('Ещё')}
        </button>
      </div>

      {more && (
        <div className="frow">
          <div className="chips" aria-label={t('Механики')}>
            {recipes.map((r) => (
              <Chip
                key={r.key}
                active={query.recipe === r.key}
                label={t(r.label)}
                count={r.count}
                onClick={() => onChange({ recipe: query.recipe === r.key ? '' : r.key })}
              />
            ))}
          </div>
          <span className="spacer" />
          <label className="sortbox">
            <span className="lab">{t('Порядок')}</span>
            <select
              className="select"
              value={query.sort}
              onChange={(e) => onChange({ sort: e.target.value as FeedSort })}
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {t(s.label)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {narrowed && (
        <div className="frow found">
          <span className="sub">
            {found > 0
              ? `${t('Нашлось')} ${num(found)} ${t(plural(found))}`
              : t('По этим условиям ничего нет')}
          </span>
          <button type="button" className="chip ghost" onClick={() => onChange(EMPTY_QUERY)}>
            <Icon name="close" size={13} />
            {t('Сбросить')}
          </button>
        </div>
      )}
    </div>
  );
}

/** Склонение для счётчика найденного. */
function plural(n: number): string {
  const mod10 = Math.abs(n) % 10;
  const mod100 = Math.abs(n) % 100;
  if (mod10 === 1 && mod100 !== 11) return 'акция';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'акции';
  return 'акций';
}

function TopicTab({
  active,
  icon,
  label,
  count,
  onClick,
}: {
  active: boolean;
  icon: IconName;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`topic ${active ? 'on' : ''}`}
      onClick={onClick}
    >
      <Icon name={icon} size={15} />
      {label}
      {count !== undefined && <span className="n">{count}</span>}
    </button>
  );
}

function Chip({
  active,
  label,
  count,
  flag,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  /** Код страны: у разрезов по странам в чипе стоит флаг. */
  flag?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`chip ${active ? 'on' : ''}`}
      onClick={onClick}
    >
      {flag && <Flag code={flag} size={11} />}
      {label}
      {count !== undefined && <span className="n">{count}</span>}
    </button>
  );
}

/**
 * Поиск с задержкой: запрос уходит через 250 мс после последнего нажатия.
 * Без неё каждая буква — отдельный запрос к ленте, и ответы приходят не в том
 * порядке, в каком их отправили.
 */
function Search({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useT();
  const [text, setText] = useState(value);
  const committed = useRef(value);

  useEffect(() => {
    // Внешнее изменение (сброс фильтров, переход по ссылке) побеждает ввод.
    if (value !== committed.current) {
      committed.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    if (text === committed.current) return;
    const timer = setTimeout(() => {
      committed.current = text;
      onChange(text);
    }, 250);
    return () => clearTimeout(timer);
  }, [text, onChange]);

  return (
    <label className="fsearch">
      <Icon name="search" size={14} />
      <input
        className="input flush"
        type="search"
        placeholder={t('Поиск по акциям и брендам')}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {text !== '' && (
        <button
          type="button"
          className="clear"
          aria-label={t('Очистить поиск')}
          onClick={() => {
            setText('');
            committed.current = '';
            onChange('');
          }}
        >
          <Icon name="close" size={13} />
        </button>
      )}
    </label>
  );
}
